// Ilustrações das cartas (janela de arte 120 × 90): os 15 loots, as cenas das
// regiões (cartas de território) e o mapa rasgado (curinga). Vetor desenhado à mão,
// no traço do kit: contorno de tinta, cor chapada, uma sombra e um brilho por forma.
import type { LootKind } from '@estandarte/engine';
import type { ReactNode } from 'react';
import { Castle, Crystal, Dune, Hill, IceSpire, INK, Mountain, Mushroom, Oak, Palm, Pine, Pyramid, Ship, Volcano, Wave } from './glyphs';

const sw = 1.6;

/** Fundo da janela de arte por categoria (gradiente escuro, como nas cartas de referência). */
export const CATEGORY_BG: Record<string, [string, string, string]> = {
  attack: ['#2a0f18', '#6e2328', '#ff9a5c'],
  defense: ['#0f2236', '#2a5a7c', '#9fd3ef'],
  control: ['#1d1433', '#4f3a7a', '#c8a8ff'],
  movement: ['#0f2a33', '#2a6470', '#9fe3e0'],
  reinforce: ['#2b1d0c', '#7a5a22', '#ffd77a'],
  cards: ['#14261a', '#3b6644', '#c9f0a0'],
  boss: ['#120d16', '#3a2230', '#ff7a5c'],
  mission: ['#2a2210', '#6b5530', '#ffe2a0'],
};

export function ArtBackdrop({ id, palette }: { id: string; palette: [string, string, string] }) {
  const [dark, mid, glow] = palette;
  return (
    <>
      <defs>
        <linearGradient id={`bg-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={dark} />
          <stop offset="1" stopColor={mid} />
        </linearGradient>
        <radialGradient id={`glow-${id}`} cx="0.5" cy="0.52" r="0.5">
          <stop offset="0" stopColor={glow} stopOpacity="0.55" />
          <stop offset="0.6" stopColor={glow} stopOpacity="0.12" />
          <stop offset="1" stopColor={glow} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="120" height="90" fill={`url(#bg-${id})`} />
      <circle cx="60" cy="46" r="42" fill={`url(#glow-${id})`} />
      {/* poeira de luz */}
      {[
        [14, 18, 0.9],
        [104, 14, 1.2],
        [22, 60, 0.7],
        [98, 52, 0.8],
        [86, 26, 0.6],
        [30, 32, 0.6],
      ].map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r} fill={glow} opacity={0.7} />
      ))}
    </>
  );
}

const Spark = ({ x, y, s = 1, c = '#fff6d8' }: { x: number; y: number; s?: number; c?: string }) => (
  <path transform={`translate(${x},${y}) scale(${s})`} d="M0,-4 L0.9,-0.9 L4,0 L0.9,0.9 L0,4 L-0.9,0.9 L-4,0 L-0.9,-0.9 Z" fill={c} />
);

const Ground = ({ y = 74, w = 34 }: { y?: number; w?: number }) => <ellipse cx={60} cy={y} rx={w} ry={4.5} fill="#000" opacity={0.28} />;

/** Pedra de runa com um glifo brilhando. */
function RuneStone({ glyph, glow }: { glyph: ReactNode; glow: string }) {
  return (
    <g>
      <Ground y={76} w={26} />
      <path d="M44,74 C40,58 41,34 47,24 C52,17 68,16 74,23 C80,32 81,58 76,74 Z" fill="#8c8aa6" stroke={INK} strokeWidth={sw} />
      <path d="M68,18 C74,22 80,32 80,52 C80,62 78,70 76,74 H64 C70,64 72,40 68,18 Z" fill="#5f5d7c" />
      <path d="M47,30 C46,40 46,52 47,60" fill="none" stroke="#c9c7dc" strokeWidth={2} strokeLinecap="round" />
      <path d="M52,70 L56,66 M70,30 L73,27" stroke={INK} strokeWidth={1} strokeLinecap="round" opacity={0.5} />
      <g filter="url(#runeGlow)">
        <g stroke={glow} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round">
          {glyph}
        </g>
      </g>
      <g stroke="#fff" strokeWidth={1} fill="none" strokeLinecap="round" strokeLinejoin="round" opacity={0.85}>
        {glyph}
      </g>
    </g>
  );
}

const RuneDefs = () => (
  <defs>
    <filter id="runeGlow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="1.8" />
    </filter>
  </defs>
);

/** Frasco de poção. */
function Flask({ liquid, dark, tall }: { liquid: string; dark: string; tall?: boolean }) {
  const body = tall
    ? 'M54,26 V38 C46,42 44,52 44,60 C44,70 51,76 60,76 C69,76 76,70 76,60 C76,52 74,42 66,38 V26 Z'
    : 'M55,28 V40 C44,44 40,52 40,60 C40,70 49,77 60,77 C71,77 80,70 80,60 C80,52 76,44 65,40 V28 Z';
  const liquidPath = tall
    ? 'M45.5,56 C52,53 68,59 74.6,55 C75.5,58 76,59 76,60 C76,70 69,76 60,76 C51,76 44,70 44,60 Z'
    : 'M41,56 C50,52 70,60 79,55 C79.6,57 80,58.5 80,60 C80,70 71,77 60,77 C49,77 40,70 40,60 Z';
  return (
    <g>
      <Ground y={78} w={24} />
      <path d={body} fill="#d7ecf2" fillOpacity={0.35} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d={liquidPath} fill={liquid} />
      <path d={liquidPath} fill={dark} clipPath="url(#flaskRight)" />
      <defs>
        <clipPath id="flaskRight">
          <rect x="62" y="40" width="30" height="40" />
        </clipPath>
      </defs>
      <path d={body} fill="none" stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <rect x={52.5} y={21} width={15} height={7} rx={2} fill="#9a6d43" stroke={INK} strokeWidth={sw} />
      <path d="M47,52 C46,58 47,64 50,68" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" opacity={0.8} />
      <circle cx={57} cy={64} r={2} fill="#fff" opacity={0.6} />
      <circle cx={64} cy={68} r={1.3} fill="#fff" opacity={0.5} />
      <circle cx={61} cy={60} r={1} fill="#fff" opacity={0.5} />
    </g>
  );
}

const LOOT_ART: Record<LootKind, () => ReactNode> = {
  strike: () => (
    <>
      <RuneDefs />
      <RuneStone
        glow="#ff6b4a"
        glyph={
          <>
            <path d="M60,34 C53,34 50,39 50.5,44 C51,47 52.5,48.6 54,49.4 V54 H66 V49.4 C67.5,48.6 69,47 69.5,44 C70,39 67,34 60,34 Z" />
            <path d="M56,43.5 H56.4 M63.6,43.5 H64 M58,54 V58 M62,54 V58" />
          </>
        }
      />
      <path d="M40,22 C34,18 34,12 38,8 M82,24 C88,20 88,14 85,10" fill="none" stroke="#2a1018" strokeWidth={3} strokeLinecap="round" opacity={0.6} />
    </>
  ),
  barrage: () => (
    <>
      <Ground y={78} w={30} />
      <path d="M38,60 C24,58 16,46 20,34 C24,42 30,44 36,42 C30,34 32,24 40,18 C40,28 46,32 52,32 C50,24 56,16 64,14 C60,22 64,28 70,30 Z" fill="#e2512d" stroke={INK} strokeWidth={sw} strokeLinejoin="round" opacity={0.9} />
      <circle cx={64} cy={50} r={20} fill="#ff9a3c" stroke={INK} strokeWidth={sw} />
      <circle cx={60} cy={46} r={13} fill="#ffd36b" />
      <circle cx={57} cy={42} r={6} fill="#fff4c8" />
      <path d="M72,64 C78,60 82,54 82,48" fill="none" stroke="#c8402a" strokeWidth={3} strokeLinecap="round" />
      <Spark x={92} y={26} s={1.2} c="#ffd36b" />
      <Spark x={86} y={70} s={0.8} c="#ffd36b" />
      <Spark x={28} y={70} s={0.9} c="#ff9a3c" />
    </>
  ),
  strength: () => (
    <>
      <Flask liquid="#d43b3b" dark="#9e2626" />
      <path d="M50,48 L54,44 M70,44 L66,48" stroke="#ffd0c4" strokeWidth={1.4} strokeLinecap="round" opacity={0.6} />
      <Spark x={86} y={30} s={1.1} c="#ffb3a0" />
      <Spark x={34} y={40} s={0.8} c="#ffb3a0" />
    </>
  ),
  precision: () => (
    <>
      <Flask liquid="#3fb2c9" dark="#2a7f94" tall />
      <circle cx={60} cy={64} r={5.6} fill="none" stroke="#e8fbff" strokeWidth={1.4} />
      <circle cx={60} cy={64} r={2} fill="#e8fbff" />
      <Spark x={84} y={28} s={1.1} c="#c8f5ff" />
      <Spark x={36} y={36} s={0.8} c="#c8f5ff" />
    </>
  ),
  blessing: () => (
    <>
      <g opacity={0.8}>
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return <path key={i} d={`M${60 + Math.cos(a) * 18},${44 + Math.sin(a) * 18} L${60 + Math.cos(a) * 34},${44 + Math.sin(a) * 34}`} stroke="#fff3c4" strokeWidth={2} strokeLinecap="round" />;
        })}
      </g>
      <Ground y={80} w={18} />
      <path d="M60,20 C66,20 69,25 69,30 C69,35 66,38 63,40 H74 V47 H63 V78 H57 V47 H46 V40 H57 C54,38 51,35 51,30 C51,25 54,20 60,20 Z M60,26 C57.6,26 56.6,28 56.6,30.4 C56.6,33 58,35 60,36.4 C62,35 63.4,33 63.4,30.4 C63.4,28 62.4,26 60,26 Z" fill="#e8c35a" stroke={INK} strokeWidth={sw} fillRule="evenodd" strokeLinejoin="round" />
      <path d="M63,47 V78 H60 V47 Z M69,40 H74 V47 H69 Z" fill="#b8902c" />
      <path d="M58.5,50 V72" stroke="#fff6cf" strokeWidth={1.3} strokeLinecap="round" />
    </>
  ),
  wall: () => (
    <>
      <Ground y={78} w={38} />
      <g stroke={INK} strokeWidth={sw} strokeLinejoin="round">
        {[0, 1, 2, 3].flatMap((row) => {
          const y = 28 + row * 12;
          const xs = row % 2 ? [28, 39, 61, 83] : [28, 50, 72];
          return xs.map((x, i) => {
            const end = i + 1 < xs.length ? xs[i + 1] : 94;
            return <rect key={`${row}-${i}`} x={x} y={y} width={end - x} height={12} fill={(row + i) % 2 ? '#8e6a5a' : '#a47c68'} />;
          });
        })}
      </g>
      <path d="M28,28 H94" stroke="#c99f8b" strokeWidth={1.4} />
      <g stroke="#8fe0ff" strokeWidth={2} fill="none" strokeLinecap="round" opacity={0.9}>
        <path d="M38,34 L42,38 L38,42" />
        <path d="M61,46 V56 M57,51 H65" />
        <path d="M82,62 C84,58 88,58 88,62" />
      </g>
      <path d="M22,20 C40,14 80,14 98,20" fill="none" stroke="#8fe0ff" strokeWidth={1.4} strokeDasharray="3 4" opacity={0.7} />
    </>
  ),
  restore: () => (
    <>
      <RuneDefs />
      <RuneStone
        glow="#7be08a"
        glyph={
          <>
            <path d="M60,34 V58 M48,46 H72" />
            <path d="M54,38 C56,36 64,36 66,38" />
          </>
        }
      />
      <Spark x={30} y={30} s={1} c="#b8ffc4" />
      <Spark x={90} y={40} s={1.3} c="#b8ffc4" />
      <Spark x={86} y={68} s={0.8} c="#b8ffc4" />
    </>
  ),
  ward: () => (
    <>
      <path d="M44,12 C46,22 52,28 60,32 C68,28 74,22 76,12" fill="none" stroke="#c9a23f" strokeWidth={2} strokeDasharray="2.6 1.6" />
      <Ground y={80} w={18} />
      <circle cx={60} cy={52} r={19} fill="#c9a23f" stroke={INK} strokeWidth={sw} />
      <circle cx={60} cy={52} r={14} fill="#8d8a80" stroke={INK} strokeWidth={sw} />
      <path d="M50,48 C52,42 58,40 63,41 M54,58 L58,55 L62,60 L67,54" fill="none" stroke="#5f5c55" strokeWidth={1.6} strokeLinecap="round" />
      <path d="M48,46 C49,42 52,40 55,39" fill="none" stroke="#cfccc2" strokeWidth={2} strokeLinecap="round" />
      <circle cx={60} cy={33} r={3} fill="#c9a23f" stroke={INK} strokeWidth={1.2} />
      <Spark x={84} y={36} c="#ffe9a8" />
    </>
  ),
  paralyze: () => (
    <>
      <RuneDefs />
      <RuneStone
        glow="#c8a8ff"
        glyph={
          <>
            <path d="M52,38 H68 L52,58 H68" />
          </>
        }
      />
      <g fill="none" stroke="#b7b2c8" strokeWidth={2.2}>
        <ellipse cx={30} cy={60} rx={5} ry={3.4} transform="rotate(-30 30 60)" />
        <ellipse cx={37} cy={55} rx={5} ry={3.4} transform="rotate(30 37 55)" />
        <ellipse cx={90} cy={60} rx={5} ry={3.4} transform="rotate(30 90 60)" />
        <ellipse cx={83} cy={55} rx={5} ry={3.4} transform="rotate(-30 83 55)" />
      </g>
    </>
  ),
  luck: () => (
    <>
      <path d="M44,10 C46,20 52,26 60,30 C68,26 74,20 76,10" fill="none" stroke="#c9a23f" strokeWidth={2} strokeDasharray="2.6 1.6" />
      <Ground y={80} w={18} />
      <circle cx={60} cy={53} r={20} fill="#c9a23f" stroke={INK} strokeWidth={sw} />
      <circle cx={60} cy={53} r={15.5} fill="#2f5d3a" stroke={INK} strokeWidth={1.2} />
      {[0, 90, 180, 270].map((a) => (
        <path key={a} transform={`rotate(${a} 60 53)`} d="M60,53 C55,49 54,42 58,40.6 C60,40 61,41.6 60,43 C61,41.6 62.6,40 64,41 C67,43 65,49 60,53 Z" fill="#6cc46a" stroke={INK} strokeWidth={1.1} />
      ))}
      <circle cx={60} cy={53} r={1.6} fill="#2f5d3a" />
      <circle cx={60} cy={31} r={3} fill="#c9a23f" stroke={INK} strokeWidth={1.2} />
      <Spark x={86} y={34} c="#fff3b0" />
      <Spark x={34} y={70} s={0.8} c="#fff3b0" />
    </>
  ),
  captain: () => (
    <>
      <Ground y={80} w={34} />
      <path d="M30,24 L86,20 L90,72 L34,76 Z" fill="#efe3c3" stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M58,22 L86,20 L90,72 L62,74 Z" fill="#dccca3" />
      <path d="M38,32 H76 M38,64 H58" stroke="#8a7350" strokeWidth={1.2} strokeLinecap="round" />
      <g transform="translate(60,54) scale(1.05)">
        <Ship s={1} />
      </g>
      <path d="M44,58 C50,55 54,61 60,58 C66,55 70,61 76,58" fill="none" stroke="#3f6e8c" strokeWidth={1.3} />
      <circle cx={80} cy={68} r={7} fill="#a23b3b" stroke={INK} strokeWidth={1.3} />
      <path d="M77,68 L80,65 L83,68 L80,71 Z" fill="#7b2626" />
    </>
  ),
  teleport: () => (
    <>
      <Ground y={80} w={34} />
      <circle cx={60} cy={44} r={20} fill="none" stroke="#7fdcff" strokeWidth={2.4} opacity={0.5} />
      <path d="M60,44 m-14,0 a14,14 0 1,1 14,14 a10,10 0 1,1 -10,-10 a6,6 0 1,1 6,6" fill="none" stroke="#bff0ff" strokeWidth={2.2} strokeLinecap="round" />
      <path d="M28,66 C28,60 32,58 36,60 H86 C90,58 94,60 94,66 C94,72 90,74 86,72 H36 C32,74 28,72 28,66 Z" fill="#efe3c3" stroke={INK} strokeWidth={sw} />
      <path d="M36,60 C32,62 32,70 36,72 M86,60 C90,62 90,70 86,72" fill="none" stroke={INK} strokeWidth={1.2} />
      <path d="M44,66 H78" stroke="#8a7350" strokeWidth={1.2} strokeDasharray="4 3" />
      <Spark x={84} y={26} s={1.2} c="#bff0ff" />
      <Spark x={34} y={30} s={0.9} c="#bff0ff" />
    </>
  ),
  reinforce: () => (
    <>
      <Ground y={80} w={34} />
      <path d="M34,22 L78,20 L80,70 L36,72 Z" fill="#efe3c3" stroke={INK} strokeWidth={sw} strokeLinejoin="round" transform="rotate(-6 57 46)" />
      <path d="M40,30 H70 M40,36 H66 M40,42 H70 M40,54 C46,50 50,58 56,52" stroke="#8a7350" strokeWidth={1.2} strokeLinecap="round" fill="none" transform="rotate(-6 57 46)" />
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <ellipse cx={80} cy={74 - i * 4.4} rx={10} ry={3.4} fill="#e8c35a" stroke={INK} strokeWidth={1.2} />
          <ellipse cx={80} cy={73 - i * 4.4} rx={6} ry={1.6} fill="#f7e09a" />
        </g>
      ))}
      <ellipse cx={96} cy={76} rx={7} ry={2.8} fill="#e8c35a" stroke={INK} strokeWidth={1.2} />
      <circle cx={54} cy={66} r={6} fill="#a23b3b" stroke={INK} strokeWidth={1.3} />
    </>
  ),
  draw: () => (
    <>
      <Ground y={80} w={38} />
      <path d="M22,26 C30,22 38,28 46,24 C54,20 62,26 70,22 C78,18 88,24 98,20 L98,70 C88,74 78,68 70,72 C62,76 54,70 46,74 C38,78 30,72 22,76 Z" fill="#efe3c3" stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M46,24 L46,74 M70,22 L70,72" stroke="#c9b68a" strokeWidth={1} />
      <path d="M30,62 C38,52 46,58 52,48 C58,40 66,46 74,38" fill="none" stroke="#a23b3b" strokeWidth={1.6} strokeDasharray="3 3" strokeLinecap="round" />
      <path d="M78,32 L86,40 M86,32 L78,40" stroke="#a23b3b" strokeWidth={2.6} strokeLinecap="round" />
      <path d="M30,40 C34,36 38,38 40,34" fill="none" stroke="#3f6e8c" strokeWidth={1.2} />
      <g transform="translate(36,70) scale(0.5)">
        <Mountain s={1} />
      </g>
    </>
  ),
  steal: () => (
    <>
      <Ground y={80} w={30} />
      <path d="M44,44 C38,52 38,74 50,78 H72 C84,74 84,52 76,44 Z" fill="#a07a4f" stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M66,44 C72,52 76,64 72,78 C80,74 84,62 76,44 Z" fill="#7a5a38" />
      <path d="M48,44 C46,38 52,34 56,38 C58,32 64,32 66,38 C70,34 76,38 72,44 Z" fill="#b48b5c" stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M46,46 H74" stroke="#5c3f24" strokeWidth={3} strokeLinecap="round" />
      <ellipse cx={34} cy={76} rx={6} ry={2.4} fill="#e8c35a" stroke={INK} strokeWidth={1.1} />
      <ellipse cx={92} cy={75} rx={5} ry={2} fill="#e8c35a" stroke={INK} strokeWidth={1.1} />
      <path d="M86,22 L100,8 M88,26 L84,22 M98,12 L102,10" stroke="#d6d9e2" strokeWidth={2.4} strokeLinecap="round" />
      <path d="M84,24 L80,28" stroke="#6b4a32" strokeWidth={3} strokeLinecap="round" />
      <Spark x={30} y={60} s={0.8} c="#fff3b0" />
    </>
  ),
};

export function LootArt({ kind }: { kind: LootKind }) {
  return <>{LOOT_ART[kind]?.()}</>;
}

/** Fundo das cenas de região: céu, chão e brilho. */
const REGION_SCENE: Record<string, { sky: [string, string]; ground: string; draw: () => ReactNode }> = {
  north: {
    sky: ['#b9d7d0', '#e8efe2'],
    ground: '#7d9a5c',
    draw: () => (
      <>
        <g transform="translate(26,64)"><Pine s={1.5} /></g>
        <g transform="translate(44,70)"><Oak s={1.5} /></g>
        <g transform="translate(80,62)"><Pine s={1.3} /></g>
        <g transform="translate(98,72)"><Pine s={1.7} /></g>
        <g transform="translate(64,76)"><Oak s={1.1} /></g>
      </>
    ),
  },
  south: {
    sky: ['#f2d9a8', '#f7ecd3'],
    ground: '#b9b779',
    draw: () => (
      <>
        <g transform="translate(30,70)"><Hill s={1.8} /></g>
        <g transform="translate(94,72)"><Hill s={1.5} /></g>
        <g transform="translate(62,66)"><Castle s={1.4} /></g>
        <g transform="translate(96,58)"><Mountain s={0.9} /></g>
      </>
    ),
  },
  ice: {
    sky: ['#c8dceb', '#eef4f8'],
    ground: '#e9f0f5',
    draw: () => (
      <>
        <g transform="translate(34,64)"><Mountain s={1.6} snow tone={['#b8c7d4', '#8ea2b4', '#e6eef4']} /></g>
        <g transform="translate(80,66)"><IceSpire s={1.8} /></g>
        <g transform="translate(100,72)"><Pine s={1.1} snow /></g>
        <g transform="translate(18,74)"><Pine s={1.2} snow /></g>
      </>
    ),
  },
  isles: {
    sky: ['#a9d4e0', '#e4f1f0'],
    ground: '#3f6e8c',
    draw: () => (
      <>
        <ellipse cx={46} cy={70} rx={28} ry={8} fill="#e3c27a" stroke={INK} strokeWidth={1.3} />
        <g transform="translate(40,68)"><Palm s={1.6} /></g>
        <g transform="translate(56,70)"><Palm s={1.1} /></g>
        <g transform="translate(94,72)"><Ship s={1.3} flip /></g>
        <g transform="translate(80,82)"><Wave s={1.3} /></g>
        <g transform="translate(20,84)"><Wave /></g>
      </>
    ),
  },
  darama: {
    sky: ['#f5c983', '#fbe9c4'],
    ground: '#e3c27a',
    draw: () => (
      <>
        <circle cx={92} cy={26} r={11} fill="#fff1c2" opacity={0.9} />
        <g transform="translate(40,66)"><Pyramid s={2} /></g>
        <g transform="translate(86,74)"><Dune s={1.6} /></g>
        <g transform="translate(22,76)"><Dune s={1.2} /></g>
        <g transform="translate(70,70)"><Palm s={1.2} /></g>
      </>
    ),
  },
  far: {
    sky: ['#4b3a6e', '#9c87b8'],
    ground: '#6f5f8a',
    draw: () => (
      <>
        <circle cx={88} cy={24} r={9} fill="#f4ecff" opacity={0.85} />
        <circle cx={92} cy={22} r={8} fill="#7a68a0" />
        <g transform="translate(36,68)"><Crystal s={1.9} /></g>
        <g transform="translate(76,70)"><Volcano s={1.2} /></g>
        <g transform="translate(58,76)"><Mushroom s={1.3} /></g>
        <g transform="translate(100,76)"><Crystal s={1} /></g>
      </>
    ),
  },
};

export function RegionArt({ region, uid }: { region: string; uid: string }) {
  const sc = REGION_SCENE[region] ?? REGION_SCENE.south;
  return (
    <>
      <defs>
        <linearGradient id={`sky-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={sc.sky[0]} />
          <stop offset="1" stopColor={sc.sky[1]} />
        </linearGradient>
      </defs>
      <rect width="120" height="90" fill={`url(#sky-${uid})`} />
      <path d="M0,58 C20,52 40,60 60,56 C80,52 100,58 120,54 V90 H0 Z" fill={sc.ground} opacity={0.9} />
      <path d="M0,70 C30,66 60,72 120,66 V90 H0 Z" fill="#000" opacity={0.08} />
      {sc.draw()}
    </>
  );
}

/** Curinga: pedaço de mapa rasgado. */
export function WildArt({ uid }: { uid: string }) {
  return (
    <>
      <defs>
        <linearGradient id={`wild-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a2a18" />
          <stop offset="1" stopColor="#7a5a32" />
        </linearGradient>
      </defs>
      <rect width="120" height="90" fill={`url(#wild-${uid})`} />
      <circle cx={60} cy={46} r={40} fill="#ffd77a" opacity={0.18} />
      <path d="M26,20 L58,16 L62,24 L56,32 L64,40 L58,52 L66,60 L60,74 L24,78 Z" fill="#efe3c3" stroke={INK} strokeWidth={1.6} strokeLinejoin="round" transform="rotate(-6 44 46)" />
      <path d="M70,18 L96,22 L94,76 L66,72 L72,60 L64,50 L72,40 L64,30 Z" fill="#e2d3ad" stroke={INK} strokeWidth={1.6} strokeLinejoin="round" transform="rotate(5 80 46)" />
      <path d="M32,60 C38,50 46,56 52,44 M74,36 C80,42 86,38 90,46" fill="none" stroke="#a23b3b" strokeWidth={1.4} strokeDasharray="3 3" />
      <g transform="translate(40,40) scale(0.6)">
        <Mountain />
      </g>
      <g transform="translate(82,64) scale(0.6)">
        <Pine />
      </g>
    </>
  );
}
